import { motion } from "framer-motion";
import { Car, DollarSign, Calculator, TrendingDown, Zap, ExternalLink, BadgePercent } from "lucide-react";

const AV_BY_LINK = "https://av.by/company/transport/3049756";

// Курс USD/BYN ~2.93 (НБРБ)
// Утилизационный сбор (Постановление Совмина №50 от 28.01.2025):
// - До 3 лет: 544 BYN = 186 USD
// - 3-7 лет: 1089 BYN = 372 USD  
// - Более 7 лет: 2757 BYN = 941 USD
// Подбор авто: 640 BYN = 218 USD

const carExamples = [
  {
    name: "Toyota Camry 2.5",
    country: "Корея",
    countryFlag: "🇰🇷",
    year: "2023",
    age: "До 3 лет",
    engine: "2494 см³",
    price: 23000,
    type: "gasoline",
    // Таможня: 48% от €21296 = €10222, но не менее €5.5*2494 = €13717 → €13717 * 1.08 = $14814
    calculation: {
      carPrice: 23000,
      customs: 14814,
      delivery: 4900,
      utilization: 186,  // до 3 лет: 544 BYN
      certification: 300,
      broker: 300,
      selection: 218,
      total: 43718,
    },
    calculation140: {
      customs: 7407,
      total: 36311,
    },
    note: "Популярный бизнес-седан",
  },
  {
    name: "Geely Monjaro 2.0T",
    country: "Китай",
    countryFlag: "🇨🇳",
    year: "2024",
    age: "До 3 лет",
    engine: "1998 см³",
    price: 27000,
    type: "gasoline",
    // Таможня: 48% от €25000 = €12000, но не менее €5.5*1998 = €10989 → €12000 * 1.08 = $12960
    calculation: {
      carPrice: 27000,
      customs: 12960,
      delivery: 2800,
      utilization: 186,  // до 3 лет: 544 BYN
      certification: 300,
      broker: 300,
      selection: 218,
      total: 43764,
    },
    calculation140: {
      customs: 6480,
      total: 37284,
    },
    note: "Премиальный кроссовер",
  },
  {
    name: "Kia K5 2.0",
    country: "Корея",
    countryFlag: "🇰🇷",
    year: "2021",
    age: "3-5 лет",
    engine: "1999 см³",
    price: 13000,
    type: "gasoline",
    // Таможня 3-5 лет: €2.7 * 1999 см³ = €5397 * 1.08 = $5829
    calculation: {
      carPrice: 13000,
      customs: 5829,
      delivery: 4900,
      utilization: 372,  // 3-7 лет: 1089 BYN
      certification: 300,
      broker: 300,
      selection: 218,
      total: 24919,
    },
    calculation140: {
      customs: 2915,
      total: 22005,
    },
    note: "Выгодный возраст 3-5 лет",
  },
  {
    name: "Tesla Model 3",
    country: "США",
    countryFlag: "🇺🇸",
    year: "2023",
    age: "До 3 лет",
    engine: "Электро",
    price: 30000,
    type: "electric",
    // Электро до 5 лет: 0% пошлина по Указу №92/428
    calculation: {
      carPrice: 30000,
      customs: 0,
      delivery: 4900,
      utilization: 186,  // до 3 лет: 544.5 BYN
      certification: 300,
      broker: 300,
      selection: 218,
      total: 35904,
    },
    calculation140: null,
    note: "0% пошлина по Указу №92/428",
    isElectric: true,
  },
  {
    name: "BYD Han EV",
    country: "Китай",
    countryFlag: "🇨🇳",
    year: "2024",
    age: "До 3 лет",
    engine: "Электро",
    price: 33000,
    type: "electric",
    // Электро до 5 лет: 0% пошлина
    calculation: {
      carPrice: 33000,
      customs: 0,
      delivery: 2800,
      utilization: 186,  // до 3 лет: 544.5 BYN
      certification: 300,
      broker: 300,
      selection: 218,
      total: 36804,
    },
    calculation140: null,
    note: "0% пошлина — электро выгоднее!",
    isElectric: true,
  },
  {
    name: "Hyundai Santa Fe 2.2D",
    country: "Корея",
    countryFlag: "🇰🇷",
    year: "2019",
    age: "Более 5 лет",
    engine: "2199 см³",
    price: 10000,
    type: "gasoline",
    // Таможня 5+ лет: €4.8 * 2199 см³ = €10555 * 1.08 = $11400
    calculation: {
      carPrice: 10000,
      customs: 11400,
      delivery: 4900,
      utilization: 941,  // более 7 лет: 2757 BYN
      certification: 300,
      broker: 300,
      selection: 218,
      total: 28059,
    },
    calculation140: {
      customs: 5700,
      total: 22359,
    },
    note: "Повышенная пошлина для 5+ лет",
    isHighDuty: true,
  },
];

export default function CarExamplesSection() {
  const scrollToCalculator = () => {
    document.querySelector("#calculator")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section 
      id="examples" 
      className="py-20 md:py-24 bg-[#0F0F0F]"
      data-testid="examples-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white/50 px-4 py-2 rounded-full text-sm font-medium mb-4 border border-white/10">
            <Calculator className="w-4 h-4" />
            Примеры расчёта
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4">
            Сколько стоит привезти авто?
          </h2>
          <p className="text-base md:text-lg text-white/50 max-w-2xl mx-auto">
            Реальные примеры расчёта стоимости популярных автомобилей с учётом всех расходов
          </p>
        </motion.div>

        {/* Examples Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {carExamples.map((car, index) => (
            <motion.div
              key={car.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className={`bg-[#0A0A0A] rounded-sm border overflow-hidden hover:shadow-xl transition-all ${
                car.isElectric 
                  ? "border-green-500/30 hover:border-green-500/50" 
                  : car.isHighDuty 
                  ? "border-red-500/30 hover:border-red-500/50"
                  : "border-white/10 hover:border-white/30"
              }`}
              data-testid={`car-example-${index}`}
            >
              {/* Header */}
              <div className={`p-4 ${
                car.isElectric 
                  ? "bg-green-500/10 border-b border-green-500/20" 
                  : car.isHighDuty
                  ? "bg-red-500/10 border-b border-red-500/20"
                  : "bg-white/5 border-b border-white/10"
              }`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{car.countryFlag}</span>
                      <span className="text-xs text-white/50">{car.country}</span>
                    </div>
                    <h3 className="text-lg font-bold text-white">{car.name}</h3>
                  </div>
                  {car.isElectric && (
                    <span className="inline-flex items-center gap-1 bg-green-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                      <Zap className="w-3 h-3" />
                      Электро
                    </span>
                  )}
                  {car.isHighDuty && (
                    <span className="inline-flex items-center gap-1 bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                      <TrendingDown className="w-3 h-3 rotate-180" />
                      5+ лет
                    </span>
                  )}
                </div>
              </div>

              {/* Specs */}
              <div className="p-4 border-b border-white/10">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-xs text-white/40">Год</p>
                    <p className="text-sm font-semibold text-white">{car.year}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/40">Возраст</p>
                    <p className="text-sm font-semibold text-white">{car.age}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/40">Двигатель</p>
                    <p className="text-sm font-semibold text-white">{car.engine}</p>
                  </div>
                </div>
              </div>

              {/* Calculation */}
              <div className="p-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/50">Стоимость авто</span>
                    <span className="font-medium">${car.calculation.carPrice.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Таможня</span>
                    <span className={`font-medium ${car.isElectric ? "text-green-400" : car.isHighDuty ? "text-red-400" : "text-white"}`}>
                      ${car.calculation.customs.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Доставка</span>
                    <span className="font-medium">${car.calculation.delivery.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Утильсбор + сертиф. + брокер</span>
                    <span className="font-medium">${(car.calculation.utilization + car.calculation.certification + car.calculation.broker).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Подбор авто</span>
                    <span className="font-medium">${car.calculation.selection}</span>
                  </div>
                </div>
                
                <div className={`mt-4 pt-4 border-t flex justify-between items-center ${
                  car.isElectric ? "border-green-500/30" : car.isHighDuty ? "border-red-500/30" : "border-white/10"
                }`}>
                  <span className="text-white/50 font-medium">Итого под ключ:</span>
                  <span className={`text-2xl font-bold ${
                    car.isElectric ? "text-green-400" : car.isHighDuty ? "text-red-400" : "text-white"
                  }`}>
                    ${car.calculation.total.toLocaleString()}
                  </span>
                </div>

                {/* Указ №140 - скидка 50% на пошлину */}
                {car.calculation140 && (
                  <div className="mt-4 pt-4 border-t border-amber-500/30 bg-amber-500/5 -mx-4 px-4 pb-2">
                    <div className="flex items-center gap-2 mb-3">
                      <BadgePercent className="w-4 h-4 text-amber-400" />
                      <span className="text-amber-400 text-xs font-semibold uppercase tracking-wider">
                        С Указом №140 (-50% пошлина)
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-sm">
                        <span className="text-white/50">Таможня: </span>
                        <span className="text-amber-400 font-medium">${car.calculation140.customs.toLocaleString()}</span>
                        <span className="text-white/30 text-xs ml-1 line-through">${car.calculation.customs.toLocaleString()}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-white/50 text-sm">Итого: </span>
                        <span className="text-amber-400 text-xl font-bold">${car.calculation140.total.toLocaleString()}</span>
                      </div>
                    </div>
                    <p className="text-amber-400/60 text-xs mt-2">
                      Экономия: ${(car.calculation.total - car.calculation140.total).toLocaleString()}
                    </p>
                  </div>
                )}
                
                <p className="mt-3 text-xs text-white/40 italic">
                  {car.note}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="text-center space-y-4"
        >
          <p className="text-white/50 mb-4">
            Хотите рассчитать стоимость для своего автомобиля?
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={scrollToCalculator}
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-200 text-black px-8 py-4 rounded-full font-semibold transition-colors"
              data-testid="examples-calc-btn"
            >
              <Calculator className="w-5 h-5" />
              Открыть калькулятор
            </button>
            <a
              href={AV_BY_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#FF6600] hover:bg-[#E55C00] text-white px-8 py-4 rounded-full font-semibold transition-colors"
              data-testid="examples-avby-btn"
            >
              <ExternalLink className="w-5 h-5" />
              Авто в наличии на av.by
            </a>
          </div>
        </motion.div>

        {/* Disclaimer */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="mt-8 text-center text-xs text-white/40"
        >
          * Цены указаны ориентировочно и могут отличаться в зависимости от актуального курса валют и стоимости конкретного автомобиля
        </motion.p>
      </div>
    </section>
  );
}
